import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import Skeleton from 'components/ui/skeleton';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { upsellClass } from 'components/portal/upsell';
import { formatDayMonth } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import { useUnlockHistory } from 'modules/examInsights/unlockHistory';
import { bestAnalysisPlan } from 'modules/portal-plans/bestAnalysisPlan';
import { usePortalPlans } from 'modules/portal-plans/usePortalPlans';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';

interface MeterProps {
  label: string;
  left: number;
  total: number;
  hint?: string;
  fill: string;
}

/** "7 / 10 qoldi" over a bar. */
function Meter({ label, left, total, hint, fill }: MeterProps) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-2 flex justify-between gap-3 text-[13.5px] font-semibold text-foreground">
        <span>{label}</span>
        <span>
          <b className="text-base tabular-nums">{left}</b> / {total} {t('subscription.left')}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={left}
        className="h-2.5 overflow-hidden rounded-full bg-border"
      >
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, (left / total) * 100)}%`, background: fill }} />
      </div>
      {hint && <p className="mt-2 text-[12.5px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Settings → Obuna: the learner's plan, analysis credits and mock tests left this period, and the next plan up. */
export default function Subscription() {
  const { t } = useTranslation();
  const { data: subscription, isLoading } = usePortalSubscription();
  const { data: plans } = usePortalPlans(true);
  const { data: history } = useUnlockHistory();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const active = Boolean(subscription && new Date(subscription.periodEnd).getTime() > Date.now());

  if (isLoading) return <Skeleton className="h-32 w-full max-w-xl rounded-lg" />;

  const analysisLimit = active ? subscription?.analysisLimit ?? 0 : 0;
  const next = bestAnalysisPlan(plans, analysisLimit);

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {checkoutOpen && <PlanCheckoutModal open onOpenChange={setCheckoutOpen} subscription={subscription} />}
      <section aria-labelledby="subscription-title" className="rounded-[22px] border border-border bg-card p-5 md:p-[26px]">
        {active && subscription ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3.5">
              <div>
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{t('subscription.current')}</span>
                <h2 id="subscription-title" className="mt-1.5 font-display text-[28px] font-extrabold tracking-[-0.03em] text-foreground">
                  {subscription.planName}
                  <span className="ml-2 inline-flex rounded-lg bg-success-50 px-2.5 py-1 align-middle text-[11.5px] font-bold tracking-normal text-success-700 dark:bg-success-900/30 dark:text-success-400">
                    {t('subscription.active')}
                  </span>
                </h2>
                <p className="mt-1.5 text-[13.5px] text-muted-foreground">
                  {t('portal_tests_plan_until', { date: formatDayMonth(subscription.periodEnd) })}
                </p>
              </div>
              <Button variant={subscription.isRenewalDue ? 'default' : 'outline'} onClick={() => setCheckoutOpen(true)}>
                {t('subscription.renew')}
              </Button>
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-2">
              {analysisLimit > 0 && (
                <Meter
                  label={t('subscription.credits')}
                  left={Math.max(0, subscription.analysesRemaining ?? 0)}
                  total={analysisLimit}
                  hint={t('subscription.credits_hint')}
                  fill="linear-gradient(90deg,#4F2BE0,#DB2777)"
                />
              )}
              {subscription.mockLimit > 0 && (
                <Meter
                  label={t('subscription.mocks')}
                  left={subscription.mocksRemaining}
                  total={subscription.mockLimit}
                  hint={t('subscription.mocks_hint')}
                  fill="hsl(var(--foreground))"
                />
              )}
            </div>
          </>
        ) : (
          <>
            <h2 id="subscription-title" className="font-display text-xl font-extrabold tracking-[-0.03em] text-foreground">
              {t('subscription.none_title')}
            </h2>
            <p className="mt-1.5 text-[13.5px] text-muted-foreground">{t('subscription.none_body')}</p>
            <button
              type="button"
              onClick={() => setCheckoutOpen(true)}
              className={cn('mt-5 inline-flex h-11 items-center rounded-xl px-5 text-sm font-bold', upsellClass)}
            >
              {t('subscription.choose')}
            </button>
          </>
        )}
      </section>

      {next && (
        <section
          aria-label={t('subscription.next_eyebrow')}
          className="relative flex flex-wrap items-center justify-between gap-5 overflow-hidden rounded-3xl bg-[#0F1226] px-6 py-6 text-white md:px-7"
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(520px_200px_at_90%_0%,rgba(219,39,119,0.45),transparent_70%),radial-gradient(420px_200px_at_5%_120%,rgba(79,43,224,0.5),transparent_70%)]"
          />
          <div className="relative max-w-[560px]">
            <span className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#E9B8FF]">{next.name}</span>
            <p className="mt-2 font-display text-2xl font-extrabold leading-[1.15] tracking-[-0.03em]">
              {t('subscription.next_title', { count: next.count })}
            </p>
            {analysisLimit > 0 && subscription && (
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#C9CCE6]">
                {t('subscription.next_body', { plan: subscription.planName, current: analysisLimit, next: next.name })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setCheckoutOpen(true)}
            className={cn('relative inline-flex h-[52px] items-center rounded-[14px] px-6 text-[15px] font-bold', upsellClass)}
          >
            {t('subscription.next_cta', { plan: next.name })}
          </button>
        </section>
      )}

      {history && history.items.length > 0 && (
        <section aria-labelledby="unlocked-title" className="rounded-[22px] border border-border bg-card p-5 md:p-[26px]">
          <h2 id="unlocked-title" className="font-display text-xl font-extrabold tracking-[-0.03em] text-foreground">
            {t('subscription.history_title')}
          </h2>
          <p className="mb-2 mt-1 text-[13px] text-muted-foreground">{t('subscription.history_used', { count: history.usedThisMonth })}</p>
          <ul className="divide-y divide-border">
            {history.items.map((item) => (
              <li key={item.attemptId}>
                <Link
                  to={`/portal/results/${item.attemptId}`}
                  className="-mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-3.5 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="min-w-0">
                    <b className="block break-words font-bold text-foreground">{item.testTitle}</b>
                    <span className="mt-0.5 block text-[12.5px] text-muted-foreground">{formatDayMonth(item.unlockedAt)}</span>
                  </span>
                  <span className="inline-flex shrink-0 rounded-lg bg-success-50 px-[9px] py-1 text-[11.5px] font-bold text-success-700 dark:bg-success-900/30 dark:text-success-400">
                    {t('subscription.history_open')}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
